import React from "react";
import { Link } from "react-router-dom";
import { Plus, Minus, ArrowRight, Trash2 } from "lucide-react";
import { useCart } from "../context/CartContext";
import { formatPrice } from "../lib/api";
import Seo from "../components/Seo";

export default function Cart() {
  const { items, updateQty, removeItem, subtotal, shipping, tax, total, count } = useCart();

  return (
    <div className="pt-32 pb-24" data-testid="cart-page">
      <Seo title="Your Bag" noindex />
      <div className="max-w-[1600px] mx-auto px-6 md:px-12">
        <div className="flex items-end justify-between mb-12">
          <div>
            <p className="eyebrow">Your Bag</p>
            <h1 className="font-display text-5xl md:text-7xl mt-3 tracking-tighter">
              {count > 0 ? `${count} piece${count === 1 ? "" : "s"}.` : "Empty."}
            </h1>
          </div>
        </div>

        {items.length === 0 ? (
          <div className="border border-dashed border-[var(--mn-border-strong)] py-32 text-center" data-testid="cart-empty-state">
            <p className="font-display text-3xl">Nothing chosen yet.</p>
            <p className="text-[var(--mn-ink-2)] mt-3">Browse the curated edit and find the bag that's right for you.</p>
            <Link to="/shop" className="btn-primary mt-8 inline-flex" data-testid="cart-empty-shop-link">
              Browse the Edit <ArrowRight size={16} strokeWidth={1.5} />
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-12 gap-8 md:gap-16">
            <div className="col-span-12 md:col-span-8">
              <ul className="divide-y divide-[var(--mn-border)] border-y border-[var(--mn-border)]">
                {items.map((it) => (
                  <li key={it.product_id} className="py-8 grid grid-cols-12 gap-6 items-center" data-testid={`cart-row-${it.product_id}`}>
                    <Link to={`/product/${it.product_id}`} className="col-span-3 md:col-span-2 aspect-[4/5] bg-neutral-100 overflow-hidden">
                      {it.image && <img src={it.image} alt={it.name} className="w-full h-full object-cover" />}
                    </Link>
                    <div className="col-span-9 md:col-span-5">
                      <Link to={`/product/${it.product_id}`} className="font-display text-2xl md:text-3xl tracking-tight hover:text-[var(--mn-accent)]">
                        {it.name}
                      </Link>
                      <p className="font-mono-luxe text-xs text-[var(--mn-ink-2)] mt-2">
                        Unit · {formatPrice(it.price)}
                      </p>
                      <button
                        onClick={() => removeItem(it.product_id)}
                        className="mt-4 text-xs uppercase tracking-[0.22em] text-[var(--mn-ink-2)] hover:text-[var(--mn-accent)] inline-flex items-center gap-2"
                        data-testid={`cart-row-remove-${it.product_id}`}
                      >
                        <Trash2 size={14} strokeWidth={1.5} /> Remove
                      </button>
                    </div>
                    <div className="col-span-7 md:col-span-3">
                      <div className="inline-flex items-center border border-[var(--mn-ink)]">
                        <button
                          onClick={() => updateQty(it.product_id, it.quantity - 1)}
                          className="w-10 h-12 flex items-center justify-center hover:bg-[var(--mn-ink)] hover:text-white transition-colors"
                          data-testid={`cart-row-decrement-${it.product_id}`}
                          aria-label="Decrease"
                        >
                          <Minus size={14} strokeWidth={1.5} />
                        </button>
                        <span className="w-10 text-center font-mono-luxe">{it.quantity}</span>
                        <button
                          onClick={() => updateQty(it.product_id, it.quantity + 1)}
                          className="w-10 h-12 flex items-center justify-center hover:bg-[var(--mn-ink)] hover:text-white transition-colors"
                          data-testid={`cart-row-increment-${it.product_id}`}
                          aria-label="Increase"
                        >
                          <Plus size={14} strokeWidth={1.5} />
                        </button>
                      </div>
                    </div>
                    <div className="col-span-5 md:col-span-2 text-right font-mono-luxe">
                      {formatPrice(it.price * it.quantity)}
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            <aside className="col-span-12 md:col-span-4 md:sticky md:top-28 self-start" data-testid="cart-summary">
              <div className="border border-[var(--mn-ink)] p-8">
                <p className="eyebrow">Summary</p>
                <h3 className="font-display text-3xl mt-2">Order total</h3>
                <dl className="mt-8 space-y-3 text-sm">
                  <Row label="Subtotal" value={formatPrice(subtotal)} testid="summary-subtotal" />
                  <Row label="Shipping" value={shipping === 0 ? "Complimentary" : formatPrice(shipping)} testid="summary-shipping" />
                  <Row label="Tax (est.)" value={formatPrice(tax)} testid="summary-tax" />
                  <div className="border-t border-[var(--mn-border)] pt-3 flex items-baseline justify-between">
                    <dt className="eyebrow">Total</dt>
                    <dd className="font-mono-luxe text-xl" data-testid="summary-total">{formatPrice(total)}</dd>
                  </div>
                </dl>
                <Link to="/checkout" className="btn-primary w-full justify-center mt-8" data-testid="cart-checkout-link">
                  Checkout <ArrowRight size={16} strokeWidth={1.5} />
                </Link>
                <p className="text-[11px] text-[var(--mn-ink-2)] mt-4 leading-relaxed">
                  Free shipping on orders over $200. White-glove delivery available at checkout.
                </p>
                <p className="text-[10px] uppercase tracking-[0.22em] text-[var(--mn-ink-2)] mt-3" data-testid="cart-us-only-notice">
                  Shipping within the United States only
                </p>
              </div>
            </aside>
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ label, value, testid }) {
  return (
    <div className="flex justify-between" data-testid={testid}>
      <dt className="text-[var(--mn-ink-2)]">{label}</dt>
      <dd className="font-mono-luxe">{value}</dd>
    </div>
  );
}
